import React, { useState } from 'react';
import { useCart } from '../context/CartContext';
import UserLayout from './UserLayout';
import { createOrder, getApiErrorMessage } from '../api/order';
import { showCustomToast } from './CustomToast';
import { useNavigate } from 'react-router-dom';
import LoadingButton from './LoadingButton';
import { resolveImageUrl } from '../utils/image';
import StatutoryComplianceNotice from './StatutoryComplianceNotice';
import './CheckoutPage.css';

export default function CheckoutPage({ user, setUser }: { user: any, setUser: (user: any) => void }) {
  const { cart, clearCart } = useCart();
  const navigate = useNavigate();
  
  const [form, setForm] = useState({
    firstName: user?.name ? user.name.split(' ')[0] : '',
    lastName: user?.name ? user.name.split(' ').slice(1).join(' ') : '',
    address: user?.address || '',
    city: user?.city || '',
    district: user?.district || '',
    state: user?.state || 'Gujarat',
    pincode: user?.pinCode || '',
    phone: user?.phoneNumber || '',
    email: user?.email || '',
    notes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [showSuccess, setShowSuccess] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'firstName': {
        const v = value.trim();
        if (!v) return 'First name is required.';
        if (v.length > 100) return 'First name cannot exceed 100 characters.';
        return '';
      }
      case 'lastName': {
        const v = value.trim();
        if (!v) return 'Last name is required.';
        if (v.length > 100) return 'Last name cannot exceed 100 characters.';
        return '';
      }
      case 'phone': {
        const digits = value.replace(/\D/g, '');
        if (!digits) return 'Mobile / WhatsApp number is required.';
        if (digits.length !== 10) return 'Mobile number must be exactly 10 digits.';
        if (!/^[6-9]\d{9}$/.test(digits)) {
          return 'Must be a valid 10-digit Indian mobile number (starts with 6, 7, 8, or 9).';
        }
        return '';
      }
      case 'email': {
        const v = value.trim();
        if (!v) return 'Email address is required.';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(v)) return 'Please enter a valid email address (e.g. name@example.com).';
        if (v.length > 100) return 'Email cannot exceed 100 characters.';
        return '';
      }
      case 'address': {
        const v = value.trim();
        if (!v) return 'Street address & landmark is required.';
        if (v.length < 5) return 'Address is too short. Please provide building/street and landmark (min 5 characters).';
        if (v.length > 200) return 'Address cannot exceed 200 characters.';
        return '';
      }
      case 'city': {
        const v = value.trim();
        if (!v) return 'Town / City is required.';
        if (v.length > 100) return 'City cannot exceed 100 characters.';
        return '';
      }
      case 'district': {
        const v = value.trim();
        if (!v) return 'District is required.';
        if (v.length > 100) return 'District cannot exceed 100 characters.';
        return '';
      }
      case 'pincode': {
        const digits = value.replace(/\D/g, '');
        if (!digits) return 'PIN Code is required.';
        if (digits.length !== 6) return 'PIN Code must be exactly 6 digits (e.g. 380001).';
        if (!/^[1-9][0-9]{5}$/.test(digits)) return 'Please enter a valid 6-digit Indian PIN Code.';
        return '';
      }
      case 'state': {
        const v = value.trim();
        if (!v) return 'State is required.';
        if (v.length > 100) return 'State cannot exceed 100 characters.';
        return '';
      }
      default:
        return '';
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let cleanValue = value;

    // Strict input filtering on keystrokes:
    if (name === 'pincode') {
      // Disallow any non-numeric characters and cap at 6 digits
      cleanValue = value.replace(/\D/g, '').slice(0, 6);
    } else if (name === 'phone') {
      // Disallow any non-numeric characters and cap at 10 digits
      cleanValue = value.replace(/\D/g, '').slice(0, 10);
    }

    setForm(prev => ({ ...prev, [name]: cleanValue }));

    if (submitError) {
      setSubmitError(null);
    }

    if (touched[name]) {
      const err = validateField(name, cleanValue);
      setErrors(prev => ({ ...prev, [name]: err }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const err = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: err }));
  };

  const MIN_ORDER_VALUE = 2000;
  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const isMinOrderMet = subtotal >= MIN_ORDER_VALUE;
  const amountNeeded = Math.max(0, MIN_ORDER_VALUE - subtotal);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (cart.length === 0) {
      showCustomToast('Your cart is empty. Please add products first.', 'error', 3000);
      return;
    }

    if (!isMinOrderMet) {
      showCustomToast(`Minimum order value must be at least ₹${MIN_ORDER_VALUE}. Add ₹${amountNeeded.toFixed(2)} more to proceed.`, 'error', 4000);
      return;
    }

    // Validate all fields
    const requiredFields = ['firstName', 'lastName', 'phone', 'email', 'address', 'city', 'district', 'pincode', 'state'];
    const newErrors: Record<string, string> = {};
    const newTouched: Record<string, boolean> = {};

    requiredFields.forEach(field => {
      newTouched[field] = true;
      const fieldError = validateField(field, (form as any)[field]);
      if (fieldError) {
        newErrors[field] = fieldError;
      }
    });

    setTouched(newTouched);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setSubmitError('Please fix the highlighted fields before submitting.');
      showCustomToast('Please correct the highlighted fields.', 'error', 3500);

      const firstErrKey = requiredFields.find(f => newErrors[f]);
      if (firstErrKey) {
        const el = document.querySelector(`[name="${firstErrKey}"]`) as HTMLElement;
        if (el) el.focus();
      }
      return;
    }

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      const orderPayload = {
        userId: user?.userId ?? null,
        firstname: form.firstName.trim(),
        lastname: form.lastName.trim(),
        country: 'India',
        streetAddress: form.address.trim(),
        city: form.city.trim(),
        district: form.district.trim(),
        state: form.state.trim(),
        pinCode: form.pincode.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        additionalInformation: form.notes?.trim() || '',
        orderItems: cart.map(item => ({
          productId: Number(item.id),
          productName: item.name,
          quantity: item.quantity,
          unitPrice: item.price,
        })),
        orderTotal: cart.reduce((sum, item) => sum + item.price * item.quantity, 0),
      };
      
      const orderId = await createOrder(orderPayload);

      // Store order details in local storage
      const orderDetails = {
        orderId: orderId,
        orderDate: new Date().toISOString(),
        customerInfo: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          address: form.address.trim(),
          city: form.city.trim(),
          district: form.district.trim(),
          state: form.state.trim(),
          pincode: form.pincode.trim(),
          country: 'India'
        },
        products: cart.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          imageUrl: item.imageUrl
        })),
        totalAmount: orderPayload.orderTotal,
        status: 'pending'
      };

      const existingOrders = JSON.parse(localStorage.getItem('userOrders') || '[]');
      existingOrders.push(orderDetails);
      localStorage.setItem('userOrders', JSON.stringify(existingOrders));

      // Clear cart from context and localStorage
      clearCart();

      // Format WhatsApp message
      const message = `
🎆 *NEW ORDER - JAI BHAVANI FIREWORKS* 🎆
--------------------------------------
*Order ID:* #${orderId}
*Customer:* ${form.firstName.trim()} ${form.lastName.trim()}
*Phone:* ${form.phone.trim()}
*Email:* ${form.email.trim() || 'N/A'}
*Customer Address:* ${form.address.trim()}, ${form.city.trim()}, ${form.district.trim() ? form.district.trim() + ', ' : ''}${form.state.trim()} - ${form.pincode.trim()}, India
*Special Notes:* ${form.notes?.trim() || 'None'}
--------------------------------------
*ITEMS ORDERED:*
${cart.map((item, idx) => `${idx + 1}. ${item.name} (Qty: ${item.quantity}) - ₹${(item.price * item.quantity).toFixed(2)}`).join('\n')}
--------------------------------------
💰 *TOTAL AMOUNT:* ₹${orderPayload.orderTotal.toFixed(2)}
*(10% Advance required for booking confirmation)*
--------------------------------------
Please confirm availability and order booking. Thank you!
      `.trim();

      // Open WhatsApp in a new tab
      window.open(
        `https://wa.me/917990696364?text=${encodeURIComponent(message)}`,
        '_blank'
      );

      setCreatedOrderId(Number(orderId));
      setShowSuccess(true);
    } catch (err: any) {
      const friendlyMessage = getApiErrorMessage(err);
      setSubmitError(friendlyMessage);
      showCustomToast(friendlyMessage, 'error', 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <UserLayout user={user} setUser={setUser} hideSlider={true}>
      <div className="checkout-page-wrapper">
        <div className="checkout-main-container">
          {/* Header */}
          <div className="checkout-top-header">
            <button className="checkout-back-link" onClick={() => navigate('/cart')}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
              </svg>
              <span>Back to Cart</span>
            </button>

            {/* Stepper Indicator */}
            <div className="checkout-steps">
              <div className="step-item completed">
                <span className="step-circle">✓</span>
                <span className="step-label">1. Cart</span>
              </div>
              <div className="step-divider active"></div>
              <div className="step-item current">
                <span className="step-circle">2</span>
                <span className="step-label">2. Customer Info</span>
              </div>
              <div className="step-divider"></div>
              <div className="step-item">
                <span className="step-circle">3</span>
                <span className="step-label">3. WhatsApp Order</span>
              </div>
            </div>
          </div>

          {/* Statutory Compliance Notice (Supreme Court 2018 Order) */}
          <StatutoryComplianceNotice 
            variant="checkout" 
            storeName="Jai Bhavani Fireworks" 
            phone="+91-7990696364" 
            whatsappNumber="917990696364" 
          />

          {/* Minimum Order Value Alert */}
          {!isMinOrderMet && (
            <div className="checkout-min-order-alert">
              <div className="alert-badge-icon">⚠️</div>
              <div className="alert-content">
                <h4>Minimum Order Value: ₹2,000 Required</h4>
                <p>
                  Your current order total is <strong>₹{subtotal.toFixed(2)}</strong>. Orders require a minimum value of <strong>₹2,000</strong> to qualify for direct wholesale fireworks pricing. Please add <strong>₹{amountNeeded.toFixed(2)}</strong> more worth of crackers to place your order.
                </p>
              </div>
              <button 
                type="button" 
                className="alert-add-more-btn" 
                onClick={() => navigate('/category')}
              >
                + Add More Crackers
              </button>
            </div>
          )}

          <div className="checkout-grid-layout">
            {/* Left: Customer & Enquiry Details Form */}
            <form onSubmit={handleSubmit} className="checkout-form-card" noValidate>
              <div className="checkout-form-header">
                <span className="form-icon">📦</span>
                <div>
                  <h2 className="form-heading">Customer &amp; Enquiry Details</h2>
                  <p className="form-subheading">Enter your contact and address details to submit your cracker enquiry for offline verification &amp; booking</p>
                </div>
              </div>

              {/* Submission Error Banner */}
              {submitError && (
                <div className="checkout-error-banner" role="alert">
                  <span className="checkout-error-banner-icon">⚠️</span>
                  <div className="checkout-error-banner-content">
                    <h4>Submission Error</h4>
                    <p>{submitError}</p>
                  </div>
                  <button 
                    type="button" 
                    className="checkout-error-banner-close"
                    onClick={() => setSubmitError(null)}
                    title="Dismiss error"
                  >
                    ✕
                  </button>
                </div>
              )}

              <div className="form-row-2">
                <div className="form-field">
                  <label htmlFor="firstName">First Name <span className="req">*</span></label>
                  <input 
                    id="firstName"
                    name="firstName" 
                    value={form.firstName} 
                    onChange={handleChange} 
                    onBlur={handleBlur}
                    required 
                    placeholder="Enter first name" 
                    className={touched.firstName && errors.firstName ? 'input-error' : ''}
                    maxLength={100}
                  />
                  {touched.firstName && errors.firstName && (
                    <span className="field-error-message">⚠️ {errors.firstName}</span>
                  )}
                </div>
                <div className="form-field">
                  <label htmlFor="lastName">Last Name <span className="req">*</span></label>
                  <input 
                    id="lastName"
                    name="lastName" 
                    value={form.lastName} 
                    onChange={handleChange} 
                    onBlur={handleBlur}
                    required 
                    placeholder="Enter last name" 
                    className={touched.lastName && errors.lastName ? 'input-error' : ''}
                    maxLength={100}
                  />
                  {touched.lastName && errors.lastName && (
                    <span className="field-error-message">⚠️ {errors.lastName}</span>
                  )}
                </div>
              </div>

              <div className="form-row-2">
                <div className="form-field">
                  <label htmlFor="phone">Mobile / WhatsApp Number <span className="req">*</span></label>
                  <input 
                    id="phone"
                    name="phone" 
                    type="tel"
                    inputMode="numeric"
                    value={form.phone} 
                    onChange={handleChange} 
                    onBlur={handleBlur}
                    required 
                    placeholder="10-digit mobile number" 
                    className={touched.phone && errors.phone ? 'input-error' : ''}
                    maxLength={10}
                  />
                  {touched.phone && errors.phone ? (
                    <span className="field-error-message">⚠️ {errors.phone}</span>
                  ) : (
                    <span className="field-hint">10 digits starting with 6, 7, 8, or 9</span>
                  )}
                </div>
                <div className="form-field">
                  <label htmlFor="email">Email Address <span className="req">*</span></label>
                  <input 
                    id="email"
                    name="email" 
                    type="email"
                    value={form.email} 
                    onChange={handleChange} 
                    onBlur={handleBlur}
                    required 
                    placeholder="name@example.com" 
                    className={touched.email && errors.email ? 'input-error' : ''}
                    maxLength={100}
                  />
                  {touched.email && errors.email && (
                    <span className="field-error-message">⚠️ {errors.email}</span>
                  )}
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="address">Street Address &amp; Landmark <span className="req">*</span></label>
                <textarea 
                  id="address"
                  name="address" 
                  value={form.address} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  required 
                  placeholder="House/Shop no., Building, Street, Area landmark..." 
                  rows={2}
                  className={touched.address && errors.address ? 'input-error' : ''}
                  maxLength={200}
                />
                {touched.address && errors.address && (
                  <span className="field-error-message">⚠️ {errors.address}</span>
                )}
              </div>

              <div className="form-row-3">
                <div className="form-field">
                  <label htmlFor="city">Town / City <span className="req">*</span></label>
                  <input 
                    id="city"
                    name="city" 
                    value={form.city} 
                    onChange={handleChange} 
                    onBlur={handleBlur}
                    required 
                    placeholder="City" 
                    className={touched.city && errors.city ? 'input-error' : ''}
                    maxLength={100}
                  />
                  {touched.city && errors.city && (
                    <span className="field-error-message">⚠️ {errors.city}</span>
                  )}
                </div>
                <div className="form-field">
                  <label htmlFor="district">District <span className="req">*</span></label>
                  <input 
                    id="district"
                    name="district" 
                    value={form.district} 
                    onChange={handleChange} 
                    onBlur={handleBlur}
                    required 
                    placeholder="District" 
                    className={touched.district && errors.district ? 'input-error' : ''}
                    maxLength={100}
                  />
                  {touched.district && errors.district && (
                    <span className="field-error-message">⚠️ {errors.district}</span>
                  )}
                </div>
                <div className="form-field">
                  <label htmlFor="pincode">PIN Code <span className="req">*</span></label>
                  <input 
                    id="pincode"
                    name="pincode" 
                    type="text"
                    inputMode="numeric"
                    value={form.pincode} 
                    onChange={handleChange} 
                    onBlur={handleBlur}
                    required 
                    placeholder="6-digit PIN" 
                    className={touched.pincode && errors.pincode ? 'input-error' : ''}
                    maxLength={6}
                  />
                  {touched.pincode && errors.pincode ? (
                    <span className="field-error-message">⚠️ {errors.pincode}</span>
                  ) : (
                    <span className="field-hint">6 numeric digits (e.g. 380001)</span>
                  )}
                </div>
              </div>

              <div className="form-row-2">
                <div className="form-field">
                  <label htmlFor="state">State <span className="req">*</span></label>
                  <input 
                    id="state"
                    name="state" 
                    value={form.state} 
                    onChange={handleChange} 
                    onBlur={handleBlur}
                    required 
                    className={touched.state && errors.state ? 'input-error' : ''}
                    maxLength={100}
                  />
                  {touched.state && errors.state && (
                    <span className="field-error-message">⚠️ {errors.state}</span>
                  )}
                </div>
                <div className="form-field">
                  <label>Country</label>
                  <input value="India (Bharat)" disabled className="input-disabled" />
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="notes">Order Notes / Special Instructions (Optional)</label>
                <textarea 
                  id="notes"
                  name="notes" 
                  value={form.notes} 
                  onChange={handleChange} 
                  placeholder="Any specific instructions or requests for your enquiry..." 
                  rows={2}
                  maxLength={500}
                />
              </div>

              <LoadingButton
                type="submit"
                onClick={() => {}}
                loading={isSubmitting}
                disabled={!isMinOrderMet || isSubmitting}
                className={`checkout-submit-button ${!isMinOrderMet ? 'disabled-min-order' : ''}`}
                title={!isMinOrderMet ? `Minimum order value of ₹2,000 required (₹${amountNeeded.toFixed(2)} needed)` : 'Submit Enquiry & Place Order'}
              >
                <span>
                  {isMinOrderMet 
                    ? 'Submit Enquiry & Place Order on WhatsApp' 
                    : `Add ₹${amountNeeded.toFixed(0)} more to Submit Enquiry`}
                </span>
                <span className="btn-arrow">&rarr;</span>
              </LoadingButton>
            </form>

            {/* Right: Order Summary */}
            <div className="checkout-summary-column">
              <div className="checkout-summary-card">
                <h3 className="checkout-summary-title">Order Summary ({cart.length} items)</h3>

                <div className="checkout-items-list">
                  {cart.map(item => {
                    const imgSrc = item.imageUrl ? resolveImageUrl(item.imageUrl) : null;
                    return (
                      <div className="checkout-item-row" key={item.id}>
                        <div className="checkout-item-img-wrap">
                          {imgSrc ? (
                            <img src={imgSrc} alt={item.name} />
                          ) : (
                            <span>🧨</span>
                          )}
                        </div>
                        <div className="checkout-item-info">
                          <span className="checkout-item-name">{item.name}</span>
                          <span className="checkout-item-qty">Qty: {item.quantity} × ₹{item.price.toFixed(2)}</span>
                        </div>
                        <span className="checkout-item-total">
                          ₹{(item.price * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div className="checkout-summary-breakdown">
                  <div className="breakdown-row">
                    <span>Subtotal</span>
                    <span>₹{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="breakdown-row discount">
                    <span>Wholesale Rate Applied</span>
                    <span>50% OFF</span>
                  </div>
                  <div className="breakdown-divider"></div>
                  <div className="breakdown-row total">
                    <strong>Total Amount</strong>
                    <strong className="total-highlight">₹{subtotal.toFixed(2)}</strong>
                  </div>

                  <div className={`checkout-min-order-pill ${isMinOrderMet ? 'met' : 'unmet'}`}>
                    {isMinOrderMet 
                      ? '✅ Wholesale Min. Order Met' 
                      : `⚠️ Min. Order ₹2,000 (₹${amountNeeded.toFixed(2)} needed)`}
                  </div>
                </div>

                {/* Important Ordering Process Notice */}
                <div className="checkout-process-box">
                  <div className="process-box-header">
                    <span className="process-icon">💬</span>
                    <strong>How WhatsApp Ordering Works:</strong>
                  </div>
                  <ol className="process-steps-list">
                    <li>Clicking <strong>Place Order</strong> will automatically open WhatsApp with your full order summary.</li>
                    <li>Our support team will verify stock and share payment details (UPI/Bank Transfer).</li>
                    <li>A <strong>10% advance payment</strong> is required to confirm your booking and lock wholesale rates.</li>
                    <li>Our team will confirm your order details and coordinate offline booking arrangements with you.</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccess && (
        <div className="success-modal-backdrop">
          <div className="success-modal-card">
            <div className="success-sparkle-icon">🎉</div>
            <h2 className="success-title">Order Enquiry Received Successfully!</h2>
            <p className="success-subtitle">
              Your offline booking enquiry has been recorded and submitted via WhatsApp. In compliance with Supreme Court 2018 regulations, our support team will contact you within 2-3 hours to verify stock and confirm order booking.
            </p>

            {createdOrderId && (
              <div className="success-order-id-box">
                <span className="id-label">YOUR ORDER ID</span>
                <span className="id-number">#{createdOrderId}</span>
                <span className="id-note">Please keep this ID handy for WhatsApp communication</span>
              </div>
            )}

            <div className="success-actions">
              <button
                onClick={() => {
                  setShowSuccess(false);
                  if (createdOrderId) navigate(`/order/${createdOrderId}`);
                }}
                className="success-btn-primary"
              >
                View Order Details
              </button>
              <button
                onClick={() => {
                  setShowSuccess(false);
                  navigate('/');
                }}
                className="success-btn-secondary"
              >
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      )}
    </UserLayout>
  );
}
